/* Basemap + terrain controls.
 *
 * Lives inside the Layers panel rather than as separate chrome: the basemap IS a
 * layer choice, and giving it its own button would add a control an operator has to
 * learn for no gain (FR-UI-04 — learnable in a day).
 */
import { Mountain, Satellite, Map as MapIcon, WifiOff } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useDeck } from '../state/store'
import { BASEMAPS, basemapAvailable, styleHasDem, type BasemapId } from '../lib/basemaps'

const ICONS: Record<BasemapId, LucideIcon> = {
  tactical: MapIcon,
  satellite: Satellite,
  'terrain-light': Mountain,
  offline: WifiOff,
}

export function BasemapPanel() {
  const basemap = useDeck((s) => s.basemap)
  const setBasemap = useDeck((s) => s.setBasemap)
  const terrain3d = useDeck((s) => s.terrain3d)
  const toggleTerrain3d = useDeck((s) => s.toggleTerrain3d)
  const exaggeration = useDeck((s) => s.terrainExaggeration)
  const setExaggeration = useDeck((s) => s.setTerrainExaggeration)
  const viewMode = useDeck((s) => s.viewMode)

  const demAvailable = styleHasDem(basemap)

  return (
    <div className="mb-1">
      <div className="label px-3 py-1">Basemap</div>

      {BASEMAPS.map((b) => {
        const Icon = ICONS[b.id]
        const active = basemap === b.id
        // In bundle mode the live sources do not exist; showing them as choosable
        // would be a lie.
        const unavailable = !basemapAvailable(b.id)
        return (
          <button
            key={b.id}
            className="flex w-full items-center gap-2 px-3 py-1 text-left transition-colors hover:bg-[var(--color-surface-2)] disabled:hover:bg-transparent"
            onClick={() => setBasemap(b.id)}
            disabled={unavailable}
            role="radio"
            aria-checked={active}
            title={unavailable ? 'Not available in bundle (air-gapped) mode' : b.note}
          >
            <Icon
              size={12}
              style={{ color: active ? 'var(--color-accent-hi)' : 'var(--color-text-3)' }}
              aria-hidden
            />
            <span
              className="min-w-0 flex-1 truncate"
              style={{ fontSize: 11.5, color: active ? 'var(--color-text-1)' : 'var(--color-text-3)' }}
            >
              {b.label}
            </span>
            {b.note && (
              <span className="label shrink-0" style={{ fontSize: 8 }}>
                {b.note}
              </span>
            )}
          </button>
        )
      })}

      {/* Terrain relief */}
      <div className="label px-3 pb-1 pt-2">Terrain relief</div>

      <button
        className="flex w-full items-center gap-2 px-3 py-1 text-left transition-colors hover:bg-[var(--color-surface-2)] disabled:hover:bg-transparent"
        onClick={toggleTerrain3d}
        disabled={!demAvailable}
        role="switch"
        aria-checked={terrain3d}
        title={
          demAvailable
            ? '3D elevation from Mapzen/AWS Terrain Tiles (public domain)'
            : 'No DEM in this basemap — production reads staged Copernicus DEM from the Atlas bundle'
        }
      >
        <Mountain
          size={12}
          style={{ color: terrain3d ? 'var(--color-accent-hi)' : 'var(--color-text-3)' }}
          aria-hidden
        />
        <span
          className="min-w-0 flex-1"
          style={{ fontSize: 11.5, color: terrain3d ? 'var(--color-text-1)' : 'var(--color-text-3)' }}
        >
          3D elevation
        </span>
        <span className="label shrink-0" style={{ fontSize: 8 }}>
          {terrain3d ? 'on' : 'off'}
        </span>
      </button>

      {terrain3d && demAvailable && (
        <div className="px-3 py-1.5">
          <div className="mb-1 flex items-center justify-between">
            <span className="label" style={{ fontSize: 8.5 }}>
              Exaggeration
            </span>
            <span className="num" style={{ fontSize: 10.5, color: 'var(--color-text-2)' }}>
              ×{exaggeration.toFixed(1)}
            </span>
          </div>
          <input
            type="range"
            min={1}
            max={4}
            step={0.1}
            value={exaggeration}
            onChange={(e) => setExaggeration(Number(e.target.value))}
            className="w-full"
            style={{ accentColor: 'var(--color-accent)' }}
            aria-label="Terrain exaggeration"
          />
          {/* Honesty about the trade-off: exaggerated relief distorts the geometry the
              operator is reading distances and LOS from. */}
          {exaggeration > 2 && (
            <p className="mt-1" style={{ fontSize: 9.5, color: 'var(--color-warn)' }}>
              Above ×2 relief is visually useful but geometrically misleading — do not read
              slope or masking off it.
            </p>
          )}
        </div>
      )}

      {viewMode === '3d' && (
        <p className="px-3 py-1" style={{ fontSize: 9.5, color: 'var(--color-text-3)' }}>
          Globe projection active. Drag to rotate, right-drag to pitch.
        </p>
      )}
    </div>
  )
}
